import React, { useState } from "react";
import { IRcaMtaStsValidationTestDomainDetails } from "../../models/IRcaMtaStsValidationTestDomainDetails";
import MtaStsValidationResultTxtRecord from "./MtaStsValidationResultTxtRecord";
import { CollapsePanel } from "../CollapsePanel";
import { Depths, mergeStyleSets, Stack, StackItem, useTheme } from "@fluentui/react";
import { IRcaMtaStsSectionDetails } from "../../models/IRcaMtaStsSectionDetails";
import { MtaStsValidationResultIcon } from "./MtaStsValidationResultIcon";
import { IRcaMtaStsValidationStatus } from "../../models/IRcaMtaStsValidationStatus";
import { MtaStsValidationResultPolicy } from "./MtaStsValidationResultPolicy";
import { MtaStsValidationResultMxRecord } from "./MtaStsValidationResultMxRecord";

/**
 * @param status The status to map into the message for the domain.
 * @returns The icon to apply.
 */
const getMessageForStatus = (status: IRcaMtaStsValidationStatus) => {
  const mapping = {
    [IRcaMtaStsValidationStatus.ConfigurationNotPresent]: WebsiteStrings.MtaStsValidationMissingConfiguration,
    [IRcaMtaStsValidationStatus.InvalidConfiguation]: WebsiteStrings.MtaStsValidationIncorrectConfiguration,
    [IRcaMtaStsValidationStatus.Success]: WebsiteStrings.MtaStsValidationSuccess,
  };

  return mapping[status];
};

/**
 * Gets the display for the domain and its status icon.
 *
 * @param domainData The domain properties
 * @returns The display for domain
 */
const getDomainDisplay = (domainData: IRcaMtaStsSectionDetails<string>): React.ReactElement => {
  return (
    <Stack horizontal verticalAlign="center" horizontalAlign="space-around" tokens={{ childrenGap: 8 }}>
      <StackItem>{domainData.Value}</StackItem>
      <MtaStsValidationResultIcon mtaStsDetails={domainData.Details} mtaStsStatus={domainData.Status} />
      <StackItem>{getMessageForStatus(domainData.Status)}</StackItem>
    </Stack>
  );
};

/**
 * The domain result UI.
 *
 * @param props - The component properties.
 * @returns The markup.
 */
const MtaStsValidationResultItem: React.FC<{
  /**
   * List of items to render in the table.
   */
  result: IRcaMtaStsValidationTestDomainDetails;
}> = ({ result }) => {
  const theme = useTheme();
  const classNames = mergeStyleSets({
    root: {
      width: "100%",
      backgroundColor: `var(--rca-card-background, ${theme.semanticColors.bodyBackground})`,
      border: "1px solid var(--rca-card-border, transparent)",
      borderRadius: 2,
      boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
      padding: 15,
      marginBottom: 20,
      maxHeight: 4000,
      height: "auto",
      transition: ".2s",
      overflow: "hidden",
    },
  });

  const [expanded, setExpanded] = useState(true);

  return (
    <Stack className={classNames.root}>
      <CollapsePanel
        expanded={expanded}
        renderTitleRow={(): React.ReactElement => getDomainDisplay(result.Domain)}
        setExpanded={setExpanded}
      >
        {result.TxtRecord?.map((value, key) => (
          <MtaStsValidationResultTxtRecord key={key} txtRecord={value} />
        ))}
        {result.ShowPolicy && <MtaStsValidationResultPolicy policy={result.Policy} />}
        {result.MxRecords?.map((value, key) => (
          <MtaStsValidationResultMxRecord key={key} mxRecord={value} />
        ))}
      </CollapsePanel>
    </Stack>
  );
};

export default MtaStsValidationResultItem;
